/**
 * KeyTooltip - Hover tooltip overlay for hex keyboard keys
 * Positioned absolutely outside SVG for better styling control
 */

export interface TooltipData {
  coord: { q: number; r: number };
  note: number;
  noteName: string;
  channel?: number | undefined;
  zoneName?: string | undefined;
  overrideType?: string | undefined;
  overrideDetail?: string | undefined;
  scaleDegree?: string | undefined;
  frequency?: string | undefined;
  pixelX: number;
  pixelY: number;
}

export interface KeyTooltipProps {
  info: TooltipData | null;
  containerWidth: number;
  containerHeight: number;
}

export function KeyTooltip({ info, containerWidth, containerHeight }: KeyTooltipProps) {
  if (!info) return null;

  const tooltipWidth = 180;
  const tooltipHeight = 120; // approximate
  const offset = 12;

  // Position: prefer right-below the key, clamp to container
  let left = info.pixelX + offset;
  let top = info.pixelY + offset;

  if (left + tooltipWidth > containerWidth) {
    left = info.pixelX - tooltipWidth - offset;
  }
  if (top + tooltipHeight > containerHeight) {
    top = info.pixelY - tooltipHeight - offset;
  }
  left = Math.max(4, left);
  top = Math.max(4, top);

  return (
    <div
      style={{
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${tooltipWidth}px`,
        backgroundColor: 'rgba(33, 37, 41, 0.95)',
        color: '#f8f9fa',
        borderRadius: '6px',
        padding: '8px 10px',
        fontSize: '11px',
        lineHeight: '1.5',
        pointerEvents: 'none',
        zIndex: 10,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '12px' }}>
        ({info.coord.q}, {info.coord.r})
      </div>
      <Row label="MIDI Note" value={`${info.note} (${info.noteName})`} />
      {info.channel !== undefined && <Row label="Channel" value={`${info.channel + 1}`} />}
      {info.zoneName && <Row label="Zone" value={info.zoneName} />}
      {info.overrideType && (
        <Row label="Override" value={`${info.overrideType}${info.overrideDetail ? ': ' + info.overrideDetail : ''}`} />
      )}
      {info.scaleDegree && <Row label="Degree" value={info.scaleDegree} />}
      {info.frequency && <Row label="Freq" value={info.frequency} />}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
      <span style={{ color: '#adb5bd' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
